<script setup lang="ts">
/*
 * Thin accent bar at the top of the viewport during slow navigations (lazy
 * route chunks, auth guards). It stays hidden for fast ones: it only appears
 * if a navigation is still pending after SHOW_DELAY_MS, trickles towards
 * TRICKLE_CEILING, jumps to 100% when the route settles, then fades.
 * Registers router guards that never block or redirect.
 */
import { onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';

/** Below this a navigation feels instant; a bar would only flicker. */
const SHOW_DELAY_MS = 120;
const TRICKLE_MS = 200;
/** Never reach 100% until the navigation really finishes. */
const TRICKLE_CEILING = 80;
const TRICKLE_STEP = 0.12;
const FADE_MS = 250;

const router = useRouter();
const visible = ref(false);
const progress = ref(0);

let showTimer: number | undefined;
let trickleTimer: number | undefined;
let fadeTimer: number | undefined;

function clearTimers() {
  window.clearTimeout(showTimer);
  window.clearInterval(trickleTimer);
  window.clearTimeout(fadeTimer);
}

function start() {
  clearTimers();
  showTimer = window.setTimeout(() => {
    progress.value = 8;
    visible.value = true;
    trickleTimer = window.setInterval(() => {
      progress.value += (TRICKLE_CEILING - progress.value) * TRICKLE_STEP;
    }, TRICKLE_MS);
  }, SHOW_DELAY_MS);
}

function finish() {
  window.clearTimeout(showTimer);
  window.clearInterval(trickleTimer);
  if (!visible.value) return;
  progress.value = 100;
  fadeTimer = window.setTimeout(() => {
    visible.value = false;
    progress.value = 0;
  }, FADE_MS);
}

const removeBefore = router.beforeEach(() => { start(); });
const removeAfter = router.afterEach(finish);
const removeError = router.onError(finish);

onBeforeUnmount(() => {
  removeBefore();
  removeAfter();
  removeError();
  clearTimers();
});
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-0 top-0 z-[90] h-[3px] transition-opacity duration-200 motion-reduce:transition-none"
    :class="visible ? 'opacity-100' : 'opacity-0'"
    aria-hidden="true"
  >
    <div
      class="h-full origin-left bg-accent transition-transform duration-200 ease-out motion-reduce:transition-none"
      :style="{ transform: `scaleX(${progress / 100})` }"
    ></div>
  </div>
</template>
